/**
 * <pub-header> custom element
 *
 * Handles scroll-inverse, mobile menu, and jump link behaviour for pub headers.
 * Supports cross-page jump links: sub-pages (Christmas, Events etc.) navigate
 * to the main pub page's sections; the main page smooth-scrolls in place.
 */
class PubHeader extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this.scrollObserver = null;
    this.announcementBar = null;
    this.scrollTracker = null;
  }

  connectedCallback() {
    this.mobileToggle = this.querySelector('[data-pub-menu-toggle]');
    this.mobileOverlay = this.querySelector('[data-pub-mobile-overlay]');
    this.jumpLinks = this.querySelectorAll('[data-pub-jump-link]');

    this.setupAnnouncementBarOffset();
    this.setupScrollInverse();
    this.setupMobileMenu();
    this.setupJumpLinks();
    this.handleInitialHash();
  }

  disconnectedCallback() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = null;
    }
    this.abortController.abort();
    document.body.classList.remove('is-pub-menu-open');
  }

  setupAnnouncementBarOffset() {
    this.announcementBar = document.querySelector('.shopify-section--announcement-bar');
    if (!this.announcementBar) return;
    const { signal } = this.abortController;
    const updateTop = () => {
      const rect = this.announcementBar.getBoundingClientRect();
      this.style.top = Math.max(0, rect.bottom) + 'px';
    };
    updateTop();
    window.addEventListener('scroll', updateTop, { signal, passive: true });
    window.addEventListener('resize', updateTop, { signal });
  }

  setupScrollInverse() {
    this.scrollTracker = document.getElementById('header-scroll-tracker')
      || document.querySelector('#main .shopify-section');
    if (!this.scrollTracker) {
      this.classList.add('is-scrolled');
      return;
    }
    this.scrollObserver = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle('is-scrolled', !entry.isIntersecting);
      },
      { threshold: 0 }
    );
    this.scrollObserver.observe(this.scrollTracker);
  }

  /**
   * Directly check the scroll tracker's current visibility and toggle
   * is-scrolled to match. Used as a safety-net after programmatic scrolls
   * where IntersectionObserver can miss the state transition.
   */
  syncScrollState() {
    if (!this.scrollTracker) return;
    const rect = this.scrollTracker.getBoundingClientRect();
    const isVisible = rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight);
    this.classList.toggle('is-scrolled', !isVisible);
  }

  setupMobileMenu() {
    if (!this.mobileToggle || !this.mobileOverlay) return;
    const { signal } = this.abortController;

    this.mobileToggle.addEventListener('click', () => this.toggleMenu(), { signal });
    this.mobileOverlay.addEventListener('click', (event) => {
      const isBackdrop = event.target === this.mobileOverlay;
      const isCloseButton = event.target.closest('[data-pub-menu-close]');
      if (isBackdrop || isCloseButton) this.closeMenu();
    }, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-menu-open')) {
        this.closeMenu();
      }
    }, { signal });
  }

  toggleMenu() {
    if (this.classList.contains('is-menu-open')) this.closeMenu();
    else this.openMenu();
  }

  openMenu() {
    this.classList.add('is-menu-open');
    this.mobileToggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('is-pub-menu-open');
    const firstFocusable = this.mobileOverlay.querySelector('a, button:not([disabled])');
    if (firstFocusable) firstFocusable.focus();
  }

  closeMenu() {
    this.classList.remove('is-menu-open');
    this.mobileToggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('is-pub-menu-open');
    this.mobileToggle.focus();
  }

  findJumpTarget(hash) {
    if (!hash || !hash.startsWith('#')) return null;
    let target = document.querySelector(hash);
    if (target) return target;

    const suffixMatch = hash.match(/(__[A-Za-z0-9_-]+)$/);
    if (suffixMatch) {
      target = document.querySelector('[id$="' + suffixMatch[1] + '"]');
    }
    return target;
  }

  parseHref(href) {
    if (!href) return { path: '', hash: '' };
    if (href.startsWith('#')) return { path: '', hash: href };
    const hashIndex = href.indexOf('#');
    if (hashIndex === -1) return { path: href, hash: '' };
    return {
      path: href.substring(0, hashIndex),
      hash: href.substring(hashIndex)
    };
  }

  isSamePage(hrefPath) {
    if (!hrefPath) return true;
    const norm = (p) => p.replace(/\/+$/, '');
    return norm(hrefPath) === norm(window.location.pathname);
  }

  scrollToTarget(target) {
    if (!target) return;
    const headerHeight = this.offsetHeight || 80;
    const targetY = target.getBoundingClientRect().top + window.scrollY - headerHeight;
    window.scrollTo({ top: targetY, behavior: 'smooth' });
  }

  setupJumpLinks() {
    const { signal } = this.abortController;

    this.jumpLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href');
        if (!href) return;

        const { path, hash } = this.parseHref(href);

        if (!this.isSamePage(path)) return;

        if (!hash) return;
        const target = this.findJumpTarget(hash);
        if (!target) return;

        event.preventDefault();

        const doScroll = () => {
          this.scrollToTarget(target);
          // Also sync scroll state after animation for consistency
          setTimeout(() => this.syncScrollState(), 700);
        };
        if (this.classList.contains('is-menu-open')) {
          this.closeMenu();
          setTimeout(doScroll, 320);
        } else {
          doScroll();
        }
      }, { signal });
    });
  }

  /**
   * On page load, if the URL has a hash, smooth-scroll to the target
   * with header offset. Also manually syncs scroll-inverse state after
   * the animation completes — IntersectionObserver can miss the state
   * transition during programmatic scrolls, so we double-check.
   */
  handleInitialHash() {
    if (!window.location.hash) return;
    setTimeout(() => {
      const target = this.findJumpTarget(window.location.hash);
      if (target) {
        this.scrollToTarget(target);
        // Sync after smooth scroll finishes (~700ms typical duration)
        setTimeout(() => this.syncScrollState(), 800);
      }
    }, 300);
  }
}

if (!customElements.get('pub-header')) {
  customElements.define('pub-header', PubHeader);
}